import { diffLines } from "diff";
import * as React from "react";
import { stringify } from "yaml";
import { fromYaml } from "../lib/manifest";
import { editablePart, reapply, userPatch } from "../lib/merge";
import { Button } from "../primitives/button";
import { Callout } from "../primitives/callout";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../primitives/dialog";
import { Textarea } from "../primitives/input";
import { CopyButton } from "./copy";

const MANAGED = ["managedFields", "resourceVersion", "uid", "generation", "creationTimestamp"] as const;

/** The object as YAML with managed metadata hidden behind a toggle and status collapsible (ui-spec §3.3). */
export function toManifestYaml(obj: Record<string, unknown>, opts: { managed: boolean; status: boolean }): string {
  const copy = structuredClone(obj) as Record<string, unknown> & { metadata?: Record<string, unknown> };
  if (!opts.managed && copy.metadata) {
    for (const k of MANAGED) delete copy.metadata[k];
  }
  if (!opts.status) delete copy.status;
  return stringify(copy, { lineWidth: 0 });
}

/** A line diff with +/- signs, so the change reads without color too (--nd-diff-* tokens). */
export function DiffView({ before, after, label }: { before: string; after: string; label: string }) {
  const parts = diffLines(before, after);
  return (
    <pre tabIndex={0} aria-label={label} className="overflow-auto rounded-md border border-line bg-sunken p-3 font-mono text-mono">
      {parts.flatMap((p, i) =>
        p.value
          .replace(/\n$/, "")
          .split("\n")
          .map((line, j) => (
            <div
              key={`${i}-${j}`}
              className={p.added ? "bg-[var(--nd-diff-add-bg)] text-[var(--nd-diff-add-fg)]" : p.removed ? "bg-[var(--nd-diff-remove-bg)] text-[var(--nd-diff-remove-fg)]" : "text-fg-secondary"}
            >
              {p.added ? "+ " : p.removed ? "- " : "  "}
              {line}
            </div>
          )),
      )}
    </pre>
  );
}

export type ApplyResult = { ok: true } | { conflict: Record<string, unknown> } | { error: string };

type Mode = { m: "view" } | { m: "edit"; text: string; error?: string } | { m: "review"; edited: Record<string, unknown> } | { m: "conflict"; edited: Record<string, unknown>; latest: Record<string, unknown> };

/**
 * View YAML (`?sheet=yaml`, `y`): the live object, Copy and Download (which keeps metadata.namespace, so the file
 * applies to the same project). With `onApply`, Edit and apply reviews the diff, then sends the user's changes with
 * the resourceVersion the sheet was opened at; a 409 shows what changed on the server and offers "Reapply my changes
 * on the latest" or Discard, never a blind overwrite.
 */
export function YamlSheet({
  open,
  onOpenChange,
  object,
  fileName,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  object: Record<string, unknown>;
  fileName: string;
  onApply?: (patch: unknown, resourceVersion: string | undefined, edited: Record<string, unknown>) => Promise<ApplyResult>;
}) {
  const [managed, setManaged] = React.useState(false);
  const [status, setStatus] = React.useState(true);
  const [mode, setMode] = React.useState<Mode>({ m: "view" });
  const [base, setBase] = React.useState(object);
  const [notice, setNotice] = React.useState<string>();
  React.useEffect(() => {
    if (mode.m === "view") setBase(object);
  }, [object, mode.m]);
  const text = React.useMemo(() => toManifestYaml(base, { managed, status }), [base, managed, status]);
  const plain = (o: Record<string, unknown>) => toManifestYaml(o, { managed: false, status: false });
  const resourceVersion = (base.metadata as { resourceVersion?: string } | undefined)?.resourceVersion;

  function download() {
    const blob = new Blob([plain(object)], { type: "application/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function apply(edited: Record<string, unknown>, opened: Record<string, unknown>, rv: string | undefined) {
    if (!onApply) return;
    const r = await onApply(userPatch(opened, edited), rv, edited);
    if ("ok" in r) {
      setNotice("Applied.");
      setMode({ m: "view" });
    } else if ("conflict" in r) setMode({ m: "conflict", edited, latest: r.conflict });
    else setNotice(r.error);
  }

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) setMode({ m: "view" }); onOpenChange(o); }}>
      <SheetContent width={720}>
        <div className="border-b border-line-subtle p-6 pr-12">
          <SheetTitle>View YAML</SheetTitle>
          <SheetDescription>The live object. Managed metadata is hidden unless you show it.</SheetDescription>
          {mode.m === "view" ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" aria-pressed={status} onClick={() => setStatus((s) => !s)}>
                {status ? "Hide status" : "Show status"}
              </Button>
              <Button variant="outline" size="sm" aria-pressed={managed} onClick={() => setManaged((m) => !m)}>
                {managed ? "Hide managed fields" : "Show managed fields"}
              </Button>
              <Button variant="outline" size="sm" onClick={download}>
                Download
              </Button>
              <CopyButton text={text} label="Copy" showLabel />
              {onApply ? (
                <Button size="sm" onClick={() => { setNotice(undefined); setMode({ m: "edit", text: plain(base) }); }}>
                  Edit and apply
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex-1 overflow-auto p-6">
          {notice ? <Callout tone="neutral" role="status" className="mb-4">{notice}</Callout> : null}
          {mode.m === "view" ? (
            <pre data-testid="yaml" tabIndex={0} aria-label="Manifest" className="rounded-md bg-sunken p-4 font-mono text-mono text-fg-secondary">
              {text}
            </pre>
          ) : null}
          {mode.m === "edit" ? (
            <div className="grid gap-3">
              <Textarea aria-label="Edit manifest" spellCheck={false} className="min-h-96 font-mono text-mono" value={mode.text} onChange={(e) => setMode({ m: "edit", text: e.target.value })} />
              {mode.error ? <p role="alert" className="text-small text-danger-fg">{mode.error}</p> : null}
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    const r = fromYaml(mode.text);
                    if ("error" in r) setMode({ ...mode, error: r.error });
                    else setMode({ m: "review", edited: r.manifest });
                  }}
                >
                  Review changes
                </Button>
                <Button variant="outline" onClick={() => setMode({ m: "view" })}>Cancel</Button>
              </div>
            </div>
          ) : null}
          {mode.m === "review" ? (
            <div className="grid gap-3">
              <DiffView label="Your changes" before={plain(editablePart(base))} after={plain(editablePart(mode.edited))} />
              <p className="text-small text-fg-muted">Only spec, labels and annotations are sent, with resourceVersion {resourceVersion ?? "—"}.</p>
              <div className="flex gap-2">
                <Button onClick={() => void apply(mode.edited, base, resourceVersion)}>Apply</Button>
                <Button variant="outline" onClick={() => setMode({ m: "edit", text: plain(mode.edited) })}>Back to editing</Button>
              </div>
            </div>
          ) : null}
          {mode.m === "conflict" ? (
            <div className="grid gap-4">
              <Callout tone="warning" role="alert" title="Someone changed this object since you opened it">
                Review what changed on the server, then reapply your edits on the latest version.
              </Callout>
              <h3 className="text-ui font-medium text-fg-primary">What the server has now</h3>
              <DiffView label="Server changes" before={plain(editablePart(base))} after={plain(editablePart(mode.latest))} />
              <h3 className="text-ui font-medium text-fg-primary">Your edits</h3>
              <DiffView label="Your edits" before={plain(editablePart(base))} after={plain(editablePart(mode.edited))} />
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    const merged = reapply(mode.latest, base, mode.edited);
                    setBase(mode.latest);
                    setMode({ m: "review", edited: merged });
                  }}
                >
                  Reapply my changes on the latest
                </Button>
                <Button variant="outline" onClick={() => { setBase(mode.latest); setMode({ m: "view" }); }}>Discard</Button>
              </div>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
